import React from "react";
import { flushSync } from "react-dom";
import _ from "lodash";
import { mat2d, vec2, glMatrix } from "gl-matrix";
import { Big } from "big.js";

import {
  getRegistrationPoint,
  updateToolMatrix,
  calculateBounds,
} from "../../utils/Transformer/tool";
import { handleTransform } from "../../utils/Transformer/handlers";
import Tool from "./Tool";
import Plugin from "./Plugin";
import {
  adjustTargetsToTool,
  scaleRotatedOutlineTargets,
  offsetTargetsFromBounds,
  adjustTargetsToBounds,
  getTargets,
  rotateGroupedTarget,
  scaleTarget,
} from "../../utils/Transformer/targets";
import {
  TransformTypes,
  Mode,
  TRANSFORMABLE_AREA_CLASSNAME,
} from "../../constants/TransformerConstants";
import { STAGE_TOP_PADDING } from "../../constants/UIConstants";
import DrawnToolStore from "../../stores/DrawnToolStore";
import SelectionStore from "../../stores/SelectionStore";
import { scaleToZoom } from "../../utils/ZoomUtils";
import UIActions from "../../actions/UIActions";
import { getCanvasBounds } from "@/utils/StageUtils";
import { redirectMove } from "@/utils/GroupUtils";

glMatrix.setMatrixArrayType(Array);
/**
 * @typedef { import("../../utils/Transformer/types").tool } tool
 * @typedef { import("@/utils/Transformer/types").Target} Target
 * @typedef { import("@/types/TransformerTypes.mjs").TransformDiff } TransformDiff
 */

const TransformAction = {
  startGlobal: vec2.create(),
  startLocal: vec2.create(),
  diffPoint: vec2.create(),
  type: null,
  handle: null,
  constrain: false,
  snapPoints: { x: [], y: [] },
};

const initState = {
  active: false,
  transforming: false,
  tool: { x: 0, y: 0, w: 0, h: 0, a: 0, matrix: [1, 0, 0, 1, 0, 0] },
  targets: {},
  targetIds: [],
  altTransform: false,
  mode: Mode.SINGLE,
  preserve: false,
  bounds: {},
  minHeight: null,
  minWidth: null,
};

// pageOffsetX/yOffset ride along on the result so what DrawnToolStore
// publishes can never drift from the offsets actually baked into the matrix
// (bounds is the same determineBounds snapshot the tool was computed from)
export const offsetTool = ({ tool, bounds }) => {
  const pageOffsetX = bounds.canvasBounds.x - bounds.containerBounds.x;
  const offsetY = bounds.canvasBounds.y - bounds.containerBounds.y;
  return {
    ...tool,
    x: pageOffsetX + tool.x,
    y: offsetY + tool.y,
    matrix: [
      ...tool.matrix.slice(0, 4),
      pageOffsetX + tool.matrix[4],
      offsetY + tool.matrix[5],
    ],
    pageOffsetX,
    yOffset: bounds.blockStart,
  };
};

class Transformer extends React.Component {
  constructor(props) {
    super(props);
    this.action = null;
    this.targets = {};
    this.outlineTargets = {};
    this.lockedTargets = {};
    this.unsubscribers = [];

    this.state = initState;

    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.UNSAFE_componentWillReceiveProps(this.props);
    const { actions } = this.props;

    this.unsubscribers.push(actions.redraw.listen(this._clearPreservation));
    this.unsubscribers.push(actions.shift.listen(this._handleShift));
    this.unsubscribers.push(UIActions.uiScaleComplete.listen(this._redraw));
    window.addEventListener("resize", this._redraw);
    this.resizeObserver = new ResizeObserver(this._remeasure);
    this.resizeObserver.observe(this.containerRef.current);
    this._addTransformListeners();
    this._publishDrawnTool();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { targetIds: propTargetIds, topOffset } = this.props;
    const { active, targetIds, tool, transforming } = this.state;
    nextProps.topOffset !== topOffset && this._clearPreservation();
    !_.isEqual(nextProps.targetIds, propTargetIds) && this._clearPreservation();

    if (!nextProps.selectedElements.length) {
      return this._resetState();
    }

    if (!transforming) {
      if (nextProps.targetIds.length > 0) {
        let init = false;
        if (nextProps.targetIds.length !== targetIds.length) {
          init = true;
        } else {
          const targetChanges = _.difference(nextProps.targetIds, targetIds);
          if (targetChanges.length > 0) {
            init = true;
          }
        }

        if (init) {
          const initTool =
            nextProps.selectedElements.length &&
            this.determineBounds(
              tool,
              nextProps.targetIds,
              true,
              nextProps.selectedElements,
            );
          if (initTool) {
            this._initTransformAction(initTool, null, TransformTypes.MOVE);
          } else {
            return;
          }
        } else {
          this.redraw();
        }

        this.setState({ active: true });
      } else {
        if (active) {
          this._resetState();
        }
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return true;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.plugin && !prevProps.plugin) {
      this._transformCancel();
    }

    const { targets, lockedTargets, transforming } = this.state;
    const { targets: prevTargets, lockedTargets: prevLockedTargets } =
      prevState;
    (!_.isEqual(targets, prevTargets) ||
      !_.isEqual(lockedTargets, prevLockedTargets)) &&
      this._clearPreservation();

    if (prevState.transforming && !transforming) {
      this.action = null;
    }

    this._publishDrawnTool();
  }

  componentWillUnmount() {
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    window.removeEventListener("resize", this._redraw);
    this.resizeObserver.disconnect();
    this._removeTransformListeners();
    cancelAnimationFrame(this.moveFrame);
    DrawnToolStore.setDrawnTool(this.props.layout, null);
  }

  /**
   * Mirrors the render condition for <Tool>: publish exactly what is drawn
   * into the SVG `<g transform>` (or null when no tool is on screen) so
   * consumers never have to read the matrix back out of the DOM. Runs in
   * the commit phase, before paint, keeping subscribers in the same paint
   * as the tool itself.
   */
  _publishDrawnTool() {
    const { plugin, layout } = this.props;
    const { active } = this.state;
    const adjustedTool = this._getAdjustedTool();
    DrawnToolStore.setDrawnTool(
      layout,
      (!plugin && active && adjustedTool) || null,
    );
  }

  /**
   * The tool with page/block offsets applied — exactly what render draws
   * into the SVG `<g transform>`.
   * @returns {object|undefined} The adjusted tool, or undefined before bounds exist.
   */
  _getAdjustedTool() {
    const { tool, bounds } = this.state;
    const { canvasBounds, containerBounds } = bounds;
    return canvasBounds && containerBounds && offsetTool({ tool, bounds });
  }

  /**
   * This function determines the bounds of the selected elements, the lock state, mode (single or multi), and updates current targetIds stored in state. This is called when the tool needs to be initially drawn or updated by transform.
   * @function determineBounds
   * @param {tool} prevTool - The current tool stored in state.
   * @param {String[]} targetIds - The ids if the selected elements.
   * */
  determineBounds = (
    prevTool,
    targetIds,
    ignorePreserve = false,
    injectedElements,
  ) => {
    const { layout, selectedElements, selectedBlockStart, zoom } = this.props;
    const { preserve } = this.state;
    const container = this.containerRef.current;
    const selectedElems = injectedElements || selectedElements;

    if (container === null || !selectedElems.length) {
      return;
    }

    const [unlockedSelectedElems, lockedSelectedElems] =
      this._sortSelectedElements(selectedElems);

    const canvasBounds = this._getCanvasBounds();
    const containerBounds = scaleToZoom(
      container.getBoundingClientRect(),
      zoom,
    );
    const idsLength = targetIds.length;

    const targetOffsets = {
      x: canvasBounds.x,
      y: containerBounds.y + selectedBlockStart,
    };

    if (selectedElems.length > 0) {
      const targets = getTargets(unlockedSelectedElems, layout, targetOffsets);
      const tool = calculateBounds(
        targets,
        prevTool,
        canvasBounds,
        !ignorePreserve ? preserve : false,
      );
      const adjustedTargets = adjustTargetsToTool(targets, canvasBounds, tool);
      tool.matrix = updateToolMatrix(tool);

      if (tool.a === 0 && idsLength > 1) {
        this.outlineTargets = { ...adjustedTargets };
      }

      if (idsLength < 2) {
        this.outlineTargets = {};
      }

      const lockedTargets = getTargets(
        lockedSelectedElems,
        layout,
        targetOffsets,
      );

      this.setState({
        bounds: {
          canvasBounds,
          containerBounds,
          // the selectedBlockStart baked into the tool via targetOffsets;
          // published with the drawn tool so consumers strip the bake-time
          // value, never a fresher prop paired with a stale matrix
          blockStart: selectedBlockStart,
        },
        tool,
        targetIds,
        mode:
          idsLength > 1 && Object.keys(targets).length > 1
            ? Mode.MULTI
            : Mode.SINGLE,
        targets: Object.keys(adjustedTargets),
        lockedTargets: Object.keys(lockedTargets),
      });

      this.targets = adjustedTargets;
      this.lockedTargets = adjustTargetsToBounds({
        targets: lockedTargets,
        bounds: canvasBounds,
      });

      return tool;
    }
    // update the targetIds since the transformer maybe redrawn once elements have been rendered in the browser
    this.setState({
      targetIds,
      mode: idsLength > 1 ? Mode.MULTI : Mode.SINGLE,
    });
  };

  /**
   * Generates a set of lists, first one being unlocked elements and second being locked elements.
   * @param {Array<object>} selectedElements
   * @returns {[Array<object>, Array<object>]}
   */
  _sortSelectedElements(selectedElements) {
    return selectedElements.reduce(
      (elems, elem) => {
        const arrayIndex = Number(elem?.lockEditing || 0);
        elems[arrayIndex].push(elem);

        return elems;
      },
      [[], []],
    );
  }

  _clientCoordsToLocal = (vec) => {
    const { zoom } = this.props;
    const { left, top } = scaleToZoom(
      this.containerRef.current.getBoundingClientRect(),
      zoom,
    );
    return vec2.subtract(vec2.create(), vec, vec2.fromValues(left, top));
  };

  _resetState = () => {
    this.action = null;
    this.targets = {};
    this.outlineTargets = {};
    this.setState(initState);
  };

  redraw = () => {
    const { active } = this.state;
    active && setTimeout(this._redraw, 0);
  };

  /**
   * The container fills the stage overlay, so it resizes whenever the
   * centered canvas can move under the tool: a zoom, or the stage itself
   * resizing (the split view gutter, panels, the window). Observer callbacks
   * run after layout and before paint, and flushSync commits the new bounds
   * there, so the tool never paints a frame at the old canvas offset the way
   * a deferred redraw does.
   */
  _remeasure = () => {
    const { active, tool, targetIds, transforming } = this.state;
    if (!active || transforming) return;
    flushSync(() => this.determineBounds(tool, targetIds));
  };

  _redraw = (clearPreservation) => {
    const { tool, targetIds } = this.state;
    clearPreservation === true && this.setState({ preserve: false });
    // wait for new state to be set before redrawing.
    setTimeout(() => this.determineBounds(tool, targetIds));
  };

  _getCanvasBounds = () => {
    const { layout, zoom = 1 } = this.props;
    return getCanvasBounds(layout, zoom);
  };

  _handleShift = (axis, amount) => {
    const { tool, preserve } = this.state;
    if (preserve) {
      this.setState({ tool: { ...tool, [axis]: tool[axis] + amount } });
      this._redraw();
    }
  };

  _clearPreservation = () => {
    const { targetIds } = this.props;
    const {
      tool: { a },
    } = this.state;
    if (a !== 0) {
      this.outlineTargets = {};
    }
    targetIds.length && this._redraw(true);
  };

  _removeTransformListeners = () => {
    window.removeEventListener("pointerdown", this._notePress, true);
    window.removeEventListener("pointermove", this._transform);
    window.removeEventListener("pointerup", this._transformEnd);
    window.removeEventListener("pointercancel", this._pointerCancel);
  };

  _addTransformListeners = () => {
    // capture: a press handler further down can't hide where the press began
    window.addEventListener("pointerdown", this._notePress, true);
    window.addEventListener("pointermove", this._transform);
    window.addEventListener("pointerup", this._transformEnd);
    // an interrupted touch keeps what it did: cancel commits like an end
    window.addEventListener("pointercancel", this._pointerCancel);
  };

  _initTransformAction = (tool, e, type, handle) => {
    const { bounds } = this.state;
    const action = _.clone(TransformAction);
    action.type = type;
    action.handle = handle;
    action.toolStart = _.cloneDeep(tool);
    action.targetsStart = _.cloneDeep(this.targets);
    action.outlineTargetsStart = _.cloneDeep(this.outlineTargets);

    if (handle) {
      _.merge(action, getRegistrationPoint(action, bounds));
    }

    if (e) {
      const { zoom } = this.props;
      action.startGlobal = vec2.fromValues(e.clientX / zoom, e.clientY / zoom);
      action.startLocal = this._clientCoordsToLocal(action.startGlobal);

      e.preventDefault();
      e.stopPropagation();
    }

    this.action = action;
  };

  _transformCancel = () => {
    // If the stores were already notified of a transform start, they must be
    // notified of the end too — their `transforming` latch otherwise sticks
    // forever and blocks every crop-mode exit path (Safari swallows the
    // mouseup of native scrollbar drags, making this cancel the only end
    // signal the stores will ever get).
    if (this.state.transforming) this._notifyTransformEnd();
    cancelAnimationFrame(this.moveFrame);
    this.moveFrame = 0;
    this.action = null;
    this.dragValid = undefined;
    this.setState({ transforming: false });
  };

  /**
   * Whether a drag at target can transform the selection: it's over the tool
   * or the selected block. A press on an unselected element selects it, so
   * its drag only gets here a frame later, once the new tool is under it.
   * @param {Element} target
   * @returns {boolean}
   */
  _startsInSelection = (target) =>
    target.closest(`.${this.props.layout}.${TRANSFORMABLE_AREA_CLASSNAME}`) !== null ||
    target.id === this.props.currentBlockId;

  /**
   * Notes whether the press began in this layout's stage. One that didn't
   * (the split view gutter, a panel) belongs to its own drag, which crosses
   * the stage before its overlay covers it. Kept on the instance, not in
   * state: the first move's frame can run before a setState from this native
   * listener is applied.
   * @param {PointerEvent} event
   */
  _notePress = (event) => {
    if (!event.isPrimary) return;
    // a pen and a finger are each their type's primary pointer: one pressing
    // while the other's transform runs doesn't take it over. Before a
    // transform runs, any press does: a finger StageGestures turns into a
    // pinch never reports its lift here, and mustn't keep the next press out.
    const owner = this.dragPointer;
    if (owner && this.state.transforming && owner.id !== event.pointerId) return;
    this.dragPointer = { id: event.pointerId };
    const target = /** @type {Element} */ (event.target);
    this.pressInStage = target.closest(`#${this.props.layout}_stage`) !== null;
  };

  /**
   * Whether e is the pointer driving the drag: the one that pressed, or with
   * no press seen, a primary pointer.
   * @param {PointerEvent} e
   * @returns {boolean}
   */
  _isDragPointer = (e) =>
    this.dragPointer ? e.pointerId === this.dragPointer.id : e.isPrimary;

  /** Releases the drag's pointer: the next press is judged afresh. */
  _releaseDragPointer = () => {
    this.dragPointer = undefined;
    this.pressInStage = undefined;
    this.dragValid = undefined;
  };

  /** @param {PointerEvent} e */
  _pointerCancel = (e) => {
    if (!this._isDragPointer(e)) return;
    this._releaseDragPointer();
    this._transformCancel();
  };

  _transform = (event) => {
    // only the drag's own pointer drives a transform; other fingers belong to stage gestures
    if (!this._isDragPointer(event)) return;
    // one frame per paint, for the latest move: every step diffs from the start
    this.pendingMove = event;
    this.moveFrame ||= requestAnimationFrame(this._applyPendingMove);
  };

  /** Applies the latest queued move: once a frame, or at once when the drag ends. */
  _applyPendingMove = () => {
    this.moveFrame = 0;
    const e = this.pendingMove;
    // Frames queued before unmount still fire after React nulls the ref
    if (!this.containerRef.current) return;
    if (this.props.plugin) return;
    // a drag pressed outside the stage never transforms, for its whole crossing
    if (this.pressInStage === false) return;

    const {
      tool,
      mode,
      altTransform,
      transforming,
      aspectRatio: stateAspectRatio,
    } = this.state;
    const {
      aspectRatio,
      constrainAngle,
      constrainArea,
      containedArea,
      layout,
      minMove,
      onDuplicateDrag,
      snapPoints,
      selectedBlockStart,
      selectedElements,
      setSnappingGuides,
      zoom,
    } = this.props;

    const outsideSelectedCanvas =
      e.target.closest(`.${layout}.${TRANSFORMABLE_AREA_CLASSNAME}`) === null;

    // judged by the move, so a cancelled frame re-judges on the next one
    if (this.dragValid === undefined && !transforming) {
      this.dragValid = this._startsInSelection(e.target);
    }

    // Bails out of transform cycle if the drag didn't start in the selection, or is a drag selection
    if (this.dragValid === false || SelectionStore.getDragSelectingState()) {
      this._transformCancel();
      return;
    }

    // Skips current step of transform process if event has no buttons down. Can happen during drag moves even if the user still has their mouse held.
    if (e.buttons === 0) {
      // Bails out entirely if cycle is running when mouse is outside of current selected block or an element "in" it
      outsideSelectedCanvas && this._transformCancel();
      return;
    }

    const { action } = this;

    if (!action) {
      this._initTransformAction(tool, e, TransformTypes.MOVE);
      return;
    }

    action.snapPoints = snapPoints;
    action.yOffset = selectedBlockStart - STAGE_TOP_PADDING;
    action.setSnappingGuides = setSnappingGuides;

    if (action.startGlobal[0] === 0 && action.startGlobal[1] === 0) {
      action.startGlobal = vec2.fromValues(
        e.clientX / zoom,
        e.clientY / zoom,
      );
      action.startLocal = this._clientCoordsToLocal(action.startGlobal);
    }

    action.diffPoint = vec2.fromValues(
      e.clientX / zoom - action.startGlobal[0],
      e.clientY / zoom - action.startGlobal[1],
    );

    if (transforming && action.constrain !== e.shiftKey) {
      action.constrain = e.shiftKey;
      if (action.constrain) {
        if (Math.abs(action.diffPoint[0]) >= Math.abs(action.diffPoint[1])) {
          action.constrainAxis = "H";
        } else {
          action.constrainAxis = "V";
        }
      }
    }

    if (transforming) {
      const { minWidth, minHeight } = selectedElements?.[0]?.[layout] || {};

      if (e.altKey && !altTransform) {
        this.setState({
          altTransform: true,
          aspectRatio: new Big(tool.w).div(tool.h).toNumber(),
        });
      } else if (altTransform) {
        this.setState({ altTransform: e.altKey });
      }

      const transformedTool = handleTransform({
        aspectRatio: aspectRatio ?? stateAspectRatio,
        tool,
        action,
        mode,
        constrainAngle,
        altTransform,
        minHeight,
        minWidth,
        constrainArea,
      });

      // secondary transform to emit to transformerStore to prevent drift
      if (containedArea) {
        const unwoundAngle = tool.a - containedArea.a;
        const containedTool = { ...tool, a: unwoundAngle };
        const redirectedDiff = redirectMove(
          action.diffPoint,
          containedArea.a,
        );
        const unwoundToolStart = { ...action.toolStart, a: unwoundAngle };
        const toolStart = {
          ...unwoundToolStart,
          matrix: updateToolMatrix(unwoundToolStart),
        };

        const containedTransform = handleTransform({
          aspectRatio: aspectRatio ?? stateAspectRatio,
          tool: { ...containedTool, matrix: updateToolMatrix(containedTool) },
          action: {
            ...action,
            diffPoint: redirectedDiff,
            toolStart,
          },
          mode,
          constrainAngle,
          altTransform,
          minHeight,
          minWidth,
          constrainArea,
          diffPoint: redirectedDiff,
        });

        this.action.containedTool = { ...tool, ...containedTransform };
        this.action.containedToolStart = toolStart;
      }

      _.merge(tool, transformedTool);

      tool.matrix = updateToolMatrix(tool);

      this._updateTargets(tool);

      if (tool.a !== 0 && action.type === TransformTypes.RESIZE) {
        const sizeDiff = tool.w / action.toolStart.w;
        this.outlineTargets = scaleRotatedOutlineTargets(
          { ...action.outlineTargetsStart },
          sizeDiff,
        );
      }

      this._finalizeTransform(tool);

      this.setState({ tool });
    } else {
      // determine if the pointer has moved enough to trigger transform action;
      // a finger's press only arrives once it has already moved to drag
      if (
        Math.abs(action.diffPoint[0]) > minMove ||
        Math.abs(action.diffPoint[1]) > minMove
      ) {
        this.setState({ transforming: true });
        this._notifyTransformStart();
        if (
          e.altKey &&
          !altTransform &&
          action.type === TransformTypes.MOVE
        ) {
          onDuplicateDrag();
        }
      }
    }
  };

  /**
   * Translates tool dimension and position updates to target
   * @param {tool} tool
   */
  _updateTargets = (tool) => {
    const { targets, action } = this;
    const { toolStart, targetsStart, type } = action;
    const { constrainAngle } = this.props;
    const { mode, preserve } = this.state;

    const scaledW = tool.w / toolStart.w;
    const scaledH = tool.h / toolStart.h;

    const scaleMatrix = mat2d.fromScaling(
      mat2d.create(),
      vec2.fromValues(scaledW, scaledH),
    );

    this.targets = Object.entries(targets).reduce(
      (updatedTargets, [id, prevTarget]) => {
        const target = { ...prevTarget };
        const targetStart = targetsStart[id];
        const isContainer = !!target.contained;
        if (!targetStart) {
          return updatedTargets;
        }

        scaleTarget({ target, targetStart, scaledW, scaledH, scaleMatrix });

        // handle differences between single select and multiselect
        if (Mode.SINGLE === mode) {
          target.a = Math.abs(tool.a < 0 ? 360 + (tool.a % 360) : tool.a % 360);
        } else {
          if (type === TransformTypes.ROTATE) {
            const { x, y, a } = rotateGroupedTarget(
              tool,
              action,
              target,
              targetStart,
              constrainAngle,
            );
            target.x = x;
            target.y = y;
            target.a = a;
            !preserve && this.setState({ preserve: true });
          } else {
            target.x = scaledW * targetStart.x;
            target.y = scaledH * targetStart.y;
          }
        }

        // applies scale to contained targets
        if (isContainer) {
          target.contained = Object.entries(target.contained).reduce(
            (updatedContainedTargets, [containedId, prevContainedTarget]) => {
              const target = { ...prevContainedTarget };
              const targetStart = targetsStart[id].contained[containedId];

              scaleTarget({
                target,
                targetStart,
                scaledW,
                scaledH,
                scaleMatrix,
              });

              target.x = scaledW * targetStart.x;
              target.y = scaledH * targetStart.y;

              return { ...updatedContainedTargets, [containedId]: target };
            },
            {},
          );
        }

        return { ...updatedTargets, [id]: target };
      },
      {},
    );
  };

  _finalizeTransform = (tool) => {
    this._notifyTransform(tool);
  };

  _notifyTransformStart = () => {
    const { onTransformStart } = this.props;
    onTransformStart && onTransformStart({ layout: this.props.layout });
  };

  _onUnlockElement = (targetId) => {
    const { unlockElement } = this.props;
    const { lockedTargets } = this.state;
    if (lockedTargets.includes(targetId)) {
      unlockElement(targetId);
    }
  };

  _transformEnd = (e) => {
    if (!this._isDragPointer(e)) return;
    // the release often lands before the last move's frame: apply that move
    // now, so the drag ends where the pointer lifted rather than running a
    // frame after it. flushSync commits any transform it starts before
    // `transforming` is read below.
    if (this.moveFrame) {
      cancelAnimationFrame(this.moveFrame);
      flushSync(this._applyPendingMove);
    }
    this.pendingMove = undefined;
    this._releaseDragPointer();
    const { altTransform, transforming } = this.state;
    if (altTransform) {
      this.setState({
        altTransform: false,
        aspectRatio: null,
      });
    }

    if (transforming) {
      this._notifyTransformEnd();
      this.setState({ transforming: false });
    }
  };

  /**
   * Generates a diff object representing the difference between the start and current position for a target.
   * @param {Target} current
   * @param {Target} prev
   * @param {{ x?: number, y?: number}=} offsets
   * @returns {TransformDiff}
   */
  _generateDiff(current, prev, { x: xOffset = 0, y: yOffset = 0 } = {}) {
    const { x, y, w, h, a } = current;
    const { x: xStart, y: yStart, w: wStart, h: hStart } = prev;

    return {
      xDiff: x - xStart + xOffset,
      yDiff: y - yStart + yOffset,
      wDiff: w - wStart,
      hDiff: h - hStart,
      a,
    };
  }

  _notifyTransform = (tool) => {
    const { targetsStart } = this.action;
    const { targets } = this;
    const { onTransform, containedArea } = this.props;

    // add tool movement offset into diffs since all targets in a local coord space
    let xOffset = tool.x - this.action.toolStart.x;
    let yOffset = tool.y - this.action.toolStart.y;

    // use contained transform when elements are in an element container. This is applies when contained elements are transformed (in a focused state).
    if (containedArea) {
      xOffset = this.action.containedTool.x - this.action.toolStart.x;
      yOffset = this.action.containedTool.y - this.action.toolStart.y;
    }

    const elems = Object.entries(targets).reduce(
      (updatedElems, [id, target]) => {
        const diff = this._generateDiff(target, targetsStart[id], {
          x: xOffset,
          y: yOffset,
        });

        // update contained targets. This applies when the container element is transformed.
        if (target.contained) {
          diff.contained = Object.entries(target.contained).reduce(
            (containedDiffs, [containedId, containedTarget]) => {
              containedDiffs[containedId] = this._generateDiff(
                containedTarget,
                targetsStart[id].contained[containedId],
              );
              return containedDiffs;
            },
            {},
          );
        }

        return { ...updatedElems, [id]: diff };
      },
      {},
    );

    onTransform && onTransform(elems);
  };

  _notifyTransformEnd = () => {
    const { onTransformEnd, setSnappingGuides } = this.props;
    onTransformEnd && onTransformEnd();
    setSnappingGuides({
      reset: true,
    });
  };

  moveStart = (allowMoveFn) => {
    const { tool, transforming } = this.state;
    const { store } = this.props;
    return (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (allowMoveFn(e) && !transforming && !store.getTransformingStatus()) {
        this._initTransformAction(tool, e, TransformTypes.MOVE);
      }
    };
  };

  resizeStart = (handle) => {
    const { tool, transforming } = this.state;
    const { store } = this.props;
    return (e) => {
      if (!transforming && !store.getTransformingStatus()) {
        this._initTransformAction(tool, e, TransformTypes.RESIZE, handle);
      }
    };
  };

  rotateStart = (handle) => {
    const { tool, transforming } = this.state;
    const { store } = this.props;
    return (e) => {
      if (!transforming && !store.getTransformingStatus()) {
        this._initTransformAction(tool, e, TransformTypes.ROTATE, handle);
      }
    };
  };

  _onDoubleClick = (e) => {
    // Check if the double-clicked target is a 'rect' element
    const selectionLayerElement = e.target.tagName === "rect";

    // If it's not the normal 'rect' element, prevent default behavior and stop propagation
    // this was added to prevent accidentally closing modals when double clicking inside of the modal
    if (!selectionLayerElement) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      const { onDoubleClick } = this.props;
      onDoubleClick && onDoubleClick(e);
    }
  };

  render() {
    const { active, action, transforming, mode, bounds } = this.state;
    const {
      plugin,
      handleMode,
      onContextMenu,
      style,
      onClickBehind,
      layout,
      scrollerRef,
      zoom = 1,
    } = this.props;
    const { canvasBounds, containerBounds } = bounds;

    const adjustedTool = this._getAdjustedTool();
    const adjustedLockedTargets =
      canvasBounds &&
      containerBounds &&
      offsetTargetsFromBounds({ targets: this.lockedTargets, bounds });

    return (
      <div
        ref={this.containerRef}
        style={style}
        onDoubleClick={this._onDoubleClick}
        onContextMenu={onContextMenu}
      >
        {!plugin && active && adjustedTool && (
          <Tool
            targets={this.outlineTargets}
            transforming={transforming}
            tool={adjustedTool}
            layout={layout}
            lockedTargets={adjustedLockedTargets}
            moveStart={this.moveStart}
            handleMode={handleMode}
            rotateStart={this.rotateStart}
            resizeStart={this.resizeStart}
            onUnlockElement={this._onUnlockElement}
            onClickBehind={onClickBehind}
            action={action}
            mode={mode}
            zoom={zoom}
            scrollerRef={scrollerRef}
          />
        )}
        {plugin && adjustedTool && (
          <Plugin {...{ tool: adjustedTool, plugin }} />
        )}
      </div>
    );
  }
}

Transformer.defaultProps = {
  idAttr: "id",
  targetIds: [],
  constrainAngle: 15,
  minMove: 2, // required to move 2 pixels before move is initiated
  zoom: 1,
};

export default Transformer;
